import React from 'react';

import { sectionsToData } from 'app/pages/workout/util/data';

import LessonSlideWrapper from 'app/ui/components/molecules/whiteboard-slide/lesson-slide-wrapper';

import type { WorkoutType } from 'app/pages/workout/util';
import { LessonSectionFormat } from 'app/types/graphql';
import LessonSectionBreakdown from 'app/ui/components/molecules/whiteboard-slide/section-breakdown';
import LessonOverview from 'app/ui/components/molecules/whiteboard-slide/lesson-overview';
import type { ContentData } from 'app/ui/components/molecules/content-item-areas-wrapper';

type MapLessonToSlidesProps = {
  workout: WorkoutType;
  isPresenterView?: boolean;
  isOverviewIncluded?: boolean;
  selectedAlternatives?: Record<string, string>;
};

export const mapLessonToSlides = ({
  workout,
  isPresenterView,
  isOverviewIncluded = true,
  selectedAlternatives = {},
}: MapLessonToSlidesProps) => {
  const lessonSections = (workout?.sections || []).filter((section) => section.format !== LessonSectionFormat.COOLDOWN);

  const sectionsData = sectionsToData({ lessonSections, dataOnly: true, selectedAlternatives });

  const slideProps = {
    lessonTitle: workout.name,
    totalSlides: sectionsData.length,
    isPresenterView,
  };

  const slides = [];
  const startIndex = 1;

  if (isOverviewIncluded) {
    slides.push(
      <LessonSlideWrapper {...slideProps} slideIndex={startIndex} isOverviewMode={true}>
        <LessonOverview sectionsData={sectionsData.filter(({ format }) => format !== LessonSectionFormat.COOLDOWN)} />
      </LessonSlideWrapper>,
    );
  }

  slides.push(
    ...sectionsData.map((sectionData, index) => (
      <LessonSlideWrapper {...slideProps} slideIndex={startIndex + index} key={`slide-${startIndex + index}`}>
        <LessonSectionBreakdown sectionData={sectionData} isPresenterView={isPresenterView} />
      </LessonSlideWrapper>
    )),
  );

  return slides;
};

export const getActiveRound = (
  elapsedTime: number,
  timeSections: { workTime: number; restTime: number } | undefined,
) => {
  if (!timeSections) {
    return 0;
  }
  const timePerRound = timeSections.workTime + timeSections.restTime;
  return Math.floor(elapsedTime / timePerRound);
};

export const getActiveSection = (
  elapsedTime: number,
  timeSections: { workTime: number; restTime: number } | undefined,
) => {
  if (!timeSections) {
    return 'Work';
  }

  const timePerRound = timeSections.workTime + timeSections.restTime;
  const timeInCurrentRound = elapsedTime % timePerRound;

  if (timeInCurrentRound < timeSections.workTime) {
    return 'Work';
  }
  return 'Rest';
};

export const calculateTotalWorkoutTime = (sectionData: ContentData): number => {
  const { workTime, restTime, rounds } = sectionData;
  return workTime && rounds ? (workTime + (restTime || 0)) * rounds : 0;
};
